import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { auth, db, storage } from '../firebase';
import { doc, getDoc, collection, query, where, onSnapshot, deleteDoc, updateDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';


// Componentes
import DashboardSidebar from '../components/dashboard/DashboardSidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import StatsCards from '../components/dashboard/StatsCards';
import AppsTable from '../components/dashboard/AppsTable';
import NotificationsPanel from '../components/dashboard/NotificationsPanel';
import ProfilePanel from '../components/dashboard/ProfilePanel';
import FinancialPanel from '../components/dashboard/FinancialPanel';
import AlertsPanel from '../components/dashboard/AlertsPanel';
import AnalyticsPanel from '../components/dashboard/AnalyticsPanel';
import SettingsPanel from '../components/dashboard/SettingsPanel';
import SupportPanel from '../components/dashboard/SupportPanel';
import LoadingScreen from '../components/dashboard/LoadingScreen';
import AlertBanner from '../components/dashboard/AlertBanner';
// eslint-disable-next-line no-unused-vars
import AppsDashboard from '../components/dashboard/AppsDashboard'; // ajuste o caminho conforme sua estrutura
import ConfirmModal from '../components/common/ConfirmModal';



// Dados de exemplo para os gráficos
const activeUsersData = [
  { name: 'Jan', usuarios: 1200 },
  { name: 'Fev', usuarios: 1900 },
  { name: 'Mar', usuarios: 2200 },
  { name: 'Abr', usuarios: 2780 },
  { name: 'Mai', usuarios: 3200 },
  { name: 'Jun', usuarios: 3900 },
  { name: 'Jul', usuarios: 4250 },
  { name: 'Ago', usuarios: 4800 },
  { name: 'Set', usuarios: 5200 },
  { name: 'Out', usuarios: 5700 },
  { name: 'Nov', usuarios: 6100 },
  { name: 'Dez', usuarios: 6500 },
];

const downloadsData = [
  { name: 'Jan', downloads: 500 },
  { name: 'Fev', downloads: 800 },
  { name: 'Mar', downloads: 1200 },
  { name: 'Abr', downloads: 1800 },
  { name: 'Mai', downloads: 2400 },
  { name: 'Jun', downloads: 3000 },
  { name: 'Jul', downloads: 3500 },
  { name: 'Ago', downloads: 4200 },
  { name: 'Set', downloads: 4800 },
  { name: 'Out', downloads: 5200 },
  { name: 'Nov', downloads: 5800 },
  { name: 'Dez', downloads: 6200 },
];

const appPlatformData = [
  { name: 'Android', value: 65 },
  { name: 'iOS', value: 35 },
];

const userRetentionData = [
  { day: '1', taxa: 100 },
  { day: '7', taxa: 75 },
  { day: '30', taxa: 50 },
  { day: '90', taxa: 35 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

function Dashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [userData, setUserData] = useState(null);
  const [userApps, setUserApps] = useState([]);
  const [adminAlerts, setAdminAlerts] = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768); // Desktop: aberto, Mobile: fechado
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [deletingAppId, setDeletingAppId] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    receiveNotifications: true,
    appVisibility: 'medium'
  });
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [appForDeletion, setAppForDeletion] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);



  const openOneSignal = () => {
    if (userData && userData.onesignalUrl) {
      window.open(userData.onesignalUrl, '_blank');
    } else {
      window.open('https://onesignal.com/', '_blank');
      showToast('URL do OneSignal não configurada. Entre em contato conosco.', 'error');
    }
  };

  const formatFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return 'Data não disponível';

    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString('pt-BR');
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString('pt-BR');
      } else if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString('pt-BR');
      } else {
        return new Date(timestamp).toLocaleString('pt-BR');
      }
    } catch (error) {
      console.error('Erro ao formatar timestamp:', error, timestamp);
      return 'Data inválida';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price || 0);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num;
  };

  const markAlertAsRead = async (alertId) => {
    try {
      await updateDoc(doc(db, 'adminAlerts', alertId), {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erro ao marcar alerta como lido:', error);
    }
  };

  const deleteSingleAlert = async (alertId) => {
    try {
      await deleteDoc(doc(db, 'adminAlerts', alertId));
      setAdminAlerts(prev => prev.filter(alert => alert.id !== alertId));
      console.log('Alerta deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar alerta:', error);
      showToast('Erro ao deletar alerta: ' + error.message, 'error');
    }
  };

  const clearAllAlerts = async () => {
    try {
      const alertsRef = collection(db, 'adminAlerts');
      const alertsSnapshot = await getDocs(alertsRef);

      const deletePromises = alertsSnapshot.docs.map(async (alertDoc) => {
        await deleteDoc(doc(db, 'adminAlerts', alertDoc.id));
      });

      await Promise.all(deletePromises);

      setAdminAlerts([]);
      setUnreadAlerts(0);

      console.log('Todos os alertas foram deletados com sucesso!');
      showToast('Todos os alertas foram limpos com sucesso!', 'success');

    } catch (error) {
      console.error('Erro ao deletar alertas:', error);
      showToast('Erro ao limpar alertas: ' + error.message, 'error');
    }
  };

  const confirmDeleteAlert = (alert) => {
    setAlertToDelete(alert);
  };

  const executeAlertDeletion = async () => {
    if (alertToDelete) {
      await deleteSingleAlert(alertToDelete.id);
      setAlertToDelete(null);
    }
  };

  const handleDeleteApp = (appId, appName) => {
    setAppForDeletion({ id: appId, name: appName });
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteApp = async () => {
    if (!appForDeletion) return;
    const appId = appForDeletion.id;

    setDeletingAppId(appId);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      const appToDelete = userApps.find(app => app.id === appId);

      if (appToDelete) {
        if (appToDelete.logo && appToDelete.logo.includes('firebasestorage.googleapis.com')) {
          try {
            const logoRef = ref(storage, appToDelete.logo);
            await deleteObject(logoRef);
          } catch (error) {
            console.warn('Erro ao deletar logo:', error);
          }
        }

        if (appToDelete.splash && appToDelete.splash.includes('firebasestorage.googleapis.com')) {
          try {
            const splashRef = ref(storage, appToDelete.splash);
            await deleteObject(splashRef);
          } catch (error) {
            console.warn('Erro ao deletar splash:', error);
          }
        }
      }

      await deleteDoc(doc(db, 'apps', appId));
      showToast('Aplicativo deletado com sucesso!', 'success');

    } catch (error) {
      console.error('Erro ao deletar aplicativo:', error);
      showToast(`Erro ao deletar aplicativo: ${error.message}`, 'error');
    } finally {
      setDeletingAppId(null);
      setIsDeleteModalOpen(false);
      setAppForDeletion(null);
    }
  };

  const updateApp = async (appId, updatedData) => {
    try {
      await updateDoc(doc(db, "apps", appId), updatedData);
      console.log("App atualizado com sucesso!");
    } catch (error) {
      console.error("Erro ao atualizar app: ", error);
      throw error;
    }
  };

  const handleProfileUpdate = (updatedData) => {
    setUserData(prev => ({ ...prev, ...updatedData }));
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Desktop: sidebar aberto por padrão, Mobile: fechado
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (locationState?.message) {
      showToast(locationState.message, 'info');
      window.history.replaceState({}, document.title);
    }
  }, [locationState]);

  useEffect(() => {
    let unsubscribe = null;
    let unsubscribeAlerts = null;

    const fetchUserDataAndApps = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }

          const appsQuery = query(
            collection(db, 'apps'),
            where('userId', '==', user.uid)
          );

          unsubscribe = onSnapshot(appsQuery, (snapshot) => {
            const appsList = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            appsList.sort((a, b) => {
              try {
                const getDate = (app) => {
                  if (!app.createdAt) return new Date(0);
                  if (app.createdAt.toDate) return app.createdAt.toDate();
                  if (app.createdAt.seconds) return new Date(app.createdAt.seconds * 1000);
                  return new Date(app.createdAt);
                };

                const dateA = getDate(a);
                const dateB = getDate(b);
                return dateB - dateA;
              } catch (error) {
                return 0;
              }
            });

            setUserApps(appsList);

            if (appsList.length > 0) {
              localStorage.setItem(`user_${user.uid}_hasApps`, 'true');
            }
          });

          const alertsQuery = query(
            collection(db, 'adminAlerts'),
            where('userId', 'in', [user.uid, 'all']),
            where('expiresAt', '>', new Date())
          );

          unsubscribeAlerts = onSnapshot(alertsQuery, (snapshot) => {
            const alertsList = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            alertsList.sort((a, b) => {
              try {
                const getDate = (alert) => {
                  if (!alert.createdAt) return new Date(0);
                  if (alert.createdAt.toDate) return alert.createdAt.toDate();
                  if (alert.createdAt.seconds) return new Date(alert.createdAt.seconds * 1000);
                  return new Date(alert.createdAt);
                };

                const dateA = getDate(a);
                const dateB = getDate(b);
                return dateB - dateA;
              } catch (error) {
                return 0;
              }
            });

            setAdminAlerts(alertsList);

            const unreadCount = alertsList.filter(alert => !alert.read).length;
            setUnreadAlerts(unreadCount);

            const newAlerts = alertsList.filter(alert => !alert.read && !alert.notified);
            if (newAlerts.length > 0) {
              newAlerts.forEach(alert => {
                updateDoc(doc(db, 'adminAlerts', alert.id), {
                  notified: true
                });
              });

              const latestAlert = newAlerts[0];

              if (activeTab !== 'alerts') {
                const notification = document.createElement('div');
                notification.className = 'fixed top-4 right-4 bg-blue-600 text-white p-4 rounded-lg shadow-lg z-50 max-w-sm';
                notification.innerHTML = `
                  <div class="flex justify-between items-start">
                    <div>
                      <h4 class="font-bold">${latestAlert.title}</h4>
                      <p class="text-sm mt-1">${latestAlert.message}</p>
                    </div>
                    <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-white">×</button>
                  </div>
                  <div class="text-right mt-2">
                    <button onclick="window.location.href='#alerts'; this.parentElement.parentElement.remove();" class="text-xs underline">
                      Ver todos os alertas
                    </button>
                  </div>
                `;
                document.body.appendChild(notification);

                setTimeout(() => {
                  if (notification.parentElement) {
                    notification.remove();
                  }
                }, 5000);
              }
            }
          });
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        if (error.code === 'permission-denied') {
          showToast('Erro de permissão. Verifique se as regras do Firestore estão configuradas corretamente.', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserDataAndApps();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (unsubscribeAlerts) {
        unsubscribeAlerts();
      }
    };
  }, [activeTab]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleNewApp = () => {
    navigate('/app-creation');
  };

  const totalInvested = userApps.reduce((total, app) => total + (app.totalPrice || 0), 0);

  const handleSettingChange = (setting, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Link copiado para a área de transferência!', 'success');
    }).catch(err => {
      console.error('Erro ao copiar texto: ', err);
      showToast('Erro ao copiar texto.', 'error');
    });
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />

            <AlertBanner
              unreadAlerts={unreadAlerts}
              handleTabClick={handleTabClick}
            />

            <StatsCards
              userApps={userApps}
              totalInvested={totalInvested}
              formatPrice={formatPrice}
            />

            <AppsTable
              userApps={userApps}
              formatFirestoreTimestamp={formatFirestoreTimestamp}
              formatPrice={formatPrice}
              handleDeleteApp={handleDeleteApp}
              deletingAppId={deletingAppId}
              handleNewApp={handleNewApp}
              updateApp={updateApp}
              showToast={showToast}
            />
          </div>
        );

      case 'apps':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <AppsTable
              userApps={userApps}
              formatFirestoreTimestamp={formatFirestoreTimestamp}
              formatPrice={formatPrice}
              handleDeleteApp={handleDeleteApp}
              deletingAppId={deletingAppId}
              handleNewApp={handleNewApp}
              updateApp={updateApp}
              showToast={showToast}
            />
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <NotificationsPanel openOneSignal={openOneSignal} />
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <ProfilePanel
              userData={userData}
              formatFirestoreTimestamp={formatFirestoreTimestamp}
              onProfileUpdate={handleProfileUpdate}
              showToast={showToast}
            />
          </div>
        );

      case 'financial':
        return (
          <div>
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <FinancialPanel
              totalInvested={totalInvested}
              formatPrice={formatPrice}
              userApps={userApps}
            />
          </div>
        );

      case 'alerts':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <AlertsPanel
              adminAlerts={adminAlerts}
              unreadAlerts={unreadAlerts}
              markAlertAsRead={markAlertAsRead}
              formatFirestoreTimestamp={formatFirestoreTimestamp}
              deleteSingleAlert={deleteSingleAlert}
              clearAllAlerts={clearAllAlerts}
              confirmDeleteAlert={confirmDeleteAlert}
            />
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <AnalyticsPanel
              activeUsersData={activeUsersData}
              downloadsData={downloadsData}
              appPlatformData={appPlatformData}
              userRetentionData={userRetentionData}
              COLORS={COLORS}
              formatNumber={formatNumber}
            />
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <SettingsPanel
              notificationSettings={notificationSettings}
              handleSettingChange={handleSettingChange}
              copyToClipboard={copyToClipboard}
              showToast={showToast}
              userApps={userApps}
            />
          </div>
        );

      case 'support':
        return (
          <div>
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <SupportPanel />
          </div>
        );

      default:
        return (
          <div>
            <DashboardHeader
              activeTab={activeTab}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isMobile={isMobile}
            />
            <div className="text-gray-800 dark:text-white">Selecione uma opção do menu</div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-[#fafafa] dark:bg-gray-900 overflow-hidden transition-colors duration-200 w-full">
      {alertToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] dark:shadow-none border border-gray-100 dark:border-gray-700 p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Deletar Alerta
            </h3>

            <p className="text-gray-600 dark:text-gray-300 font-light mb-4 text-sm">
              Tem certeza que deseja deletar o alerta "{alertToDelete.title}"?
            </p>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800/30 rounded-xl p-4 mb-6">
              <p className="text-yellow-800 dark:text-yellow-400 text-sm font-medium">
                Esta ação não pode ser desfeita.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAlertToDelete(null)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={executeAlertDeletion}
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors shadow-sm"
              >
                Deletar Alerta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Layout Principal com Sidebar e Conteúdo */}
      {/* Sidebar - Desktop: fixo na lateral, Mobile: overlay ao clicar */}
      <DashboardSidebar
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        unreadAlerts={unreadAlerts}
        isMobile={isMobile}
      />

      {/* Área de Conteúdo Principal */}
      <div className="
          flex-1 
          w-full
          p-4 sm:p-6 md:p-8 lg:p-10
          overflow-y-auto custom-scrollbar
        ">
        <div className="max-w-7xl mx-auto w-full">
          <div className="hidden md:block">
            <Navbar onProfileClick={() => setSidebarOpen(true)} />
          </div>
          {renderTabContent()}
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAppForDeletion(null);
        }}
        onConfirm={confirmDeleteApp}
        title="Excluir Aplicativo"
        message={`Tem certeza que deseja deletar o aplicativo "${appForDeletion?.name}"? Esta ação não pode ser desfeita.`}
        warningText="O valor não será reembolsado automaticamente. Para solicitar reembolso, entre em contato conosco."
        confirmText="Excluir Aplicativo"
        isLoading={deletingAppId !== null}
      />

      {/* Custom Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-gray-900/90 dark:bg-white/95 backdrop-blur-md text-white dark:text-gray-950 px-4 py-3.5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-800 dark:border-gray-100 transition-all duration-300 fade-in">
          {toast.type === 'success' && (
            <span className="bg-emerald-500/10 text-emerald-500 p-1 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
          )}
          {toast.type === 'error' && (
            <span className="bg-red-500/10 text-red-500 p-1 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          {toast.type === 'info' && (
            <span className="bg-blue-500/10 text-blue-500 p-1 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
          )}
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}

export default Dashboard;